import { Card, CardContent, EmptyState } from '@adop/ui';
import { ScrollText } from 'lucide-react';
import { useWorkspace } from '../../lib/workspace-context';
import { useAsync } from '../../lib/useAsync';
import { listAuditLog, listProfilesByIds, type ProfileRow } from '../../lib/data';
import { activityActorLabel, relativeTime } from '../../lib/format';
import { Alert } from '../../components/Alert';
import { PageHeader } from '../../components/PageHeader';
import { ListSkeleton } from '../../components/LoadingState';

interface ActivityData {
  entries: Awaited<ReturnType<typeof listAuditLog>>;
  profiles: Record<string, ProfileRow>;
}

export function ActivityPage() {
  const { restaurant, canOwn } = useWorkspace();
  const state = useAsync<ActivityData | null>(async () => {
    if (!restaurant || !canOwn) return null;
    const entries = await listAuditLog(restaurant.id);
    const actorIds = entries
      .map((entry) => entry.actor_user_id)
      .filter((id): id is string => typeof id === 'string');
    const profiles = await listProfilesByIds([...new Set(actorIds)]);
    return {
      entries,
      profiles: Object.fromEntries(profiles.map((profile) => [profile.id, profile])),
    };
  }, [restaurant?.id, canOwn]);

  if (!restaurant || !canOwn) {
    return (
      <EmptyState
        icon={<ScrollText className="h-6 w-6" />}
        title="Owners only"
        description="The activity log is visible to restaurant owners."
      />
    );
  }

  return (
    <div className="space-y-5">
      <PageHeader title="Activity" description="A record of important changes to your restaurant." />

      {state.error ? <Alert>{state.error}</Alert> : null}

      {state.loading ? (
        <ListSkeleton rows={6} />
      ) : (state.data?.entries ?? []).length === 0 ? (
        <EmptyState icon={<ScrollText className="h-6 w-6" />} title="No activity yet" />
      ) : (
        <Card>
          <CardContent className="p-0">
            <ul className="divide-y divide-zinc-100">
              {(state.data?.entries ?? []).map((entry) => {
                const actor = entry.actor_user_id ? state.data?.profiles[entry.actor_user_id] : null;
                return (
                  <li key={entry.id} className="flex flex-wrap items-center gap-3 px-5 py-3 text-sm">
                    <span className="font-medium text-zinc-900">{entry.action}</span>
                    {entry.target ? <span className="truncate text-zinc-500">{entry.target}</span> : null}
                    <span className="ml-auto text-xs text-zinc-500">
                      {activityActorLabel(entry.actor_user_id, actor?.full_name)} ·{' '}
                      {relativeTime(entry.created_at)}
                    </span>
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
